// src/App.js
import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AppProvider } from './context/AppContext';

// ── Public pages ──────────────────────────────────────────────────
import Navbar         from './components/Navbar';
import Footer         from './components/Footer';
import CartDrawer     from './components/CartDrawer';
import HomePage       from './pages/HomePage';
import ProductsPage   from './pages/ProductsPage';
import ProductDetailPage from './pages/ProductDetailPage';
import AuthPage       from './pages/AuthPage';
import CheckoutPage   from './pages/CheckoutPage';
import OrdersPage     from './pages/OrdersPage';
import TrackingPage   from './pages/TrackingPage';
import WishlistPage   from './pages/WishlistPage';
import PolicyPage     from './pages/PolicyPage';
import BecomeMerchantPage from './pages/BecomeMerchantPage';
import ReturnsPage    from './pages/ReturnsPage';

// ── Merchant dashboard ────────────────────────────────────────────
import MerchantRoute       from './merchant/MerchantRoute';
import MerchantLayout      from './merchant/MerchantLayout';
import MerchantDashboard   from './merchant/MerchantDashboard';
import MerchantProducts    from './merchant/MerchantProducts';
import MerchantProductForm from './merchant/MerchantProductForm';
import MerchantOrders      from './merchant/MerchantOrders';
import MerchantEarnings    from './merchant/MerchantEarnings';
import MerchantProfile     from './merchant/MerchantProfile';

// ── Admin panel ───────────────────────────────────────────────────
import AdminRoute      from './admin/AdminRoute';
import AdminLayout     from './admin/AdminLayout';
import AdminDashboard  from './admin/AdminDashboard';
import AdminMerchants  from './admin/AdminMerchants';
import AdminOrders     from './admin/AdminOrders';
import AdminProducts   from './admin/AdminProducts';
import AdminCoupons    from './admin/AdminCoupons';
import AdminReturns    from './admin/AdminReturns';
import AdminCEOAgent   from './admin/AdminCEOAgent';

// ── Layout wrapper — hides Navbar/Footer on dashboard routes ──────
function AppLayout() {
  const { pathname } = useLocation();

  // Hide main Navbar and Footer on merchant/admin pages
  // These have their OWN sidebar navigation
  const isDashboard =
    pathname.startsWith('/merchant') ||
    pathname.startsWith('/admin');

  return (
    <>
      {/* Main site Navbar — hidden on dashboard pages */}
      {!isDashboard && <Navbar />}

      {/* Cart drawer — only on public pages */}
      {!isDashboard && <CartDrawer />}

      <Routes>
        {/* ── Public routes ── */}
        <Route path="/"            element={<HomePage />}        />
        <Route path="/products"    element={<ProductsPage />}    />
        <Route path="/product/:id" element={<ProductDetailPage />} />
        <Route path="/login"       element={<AuthPage />}        />
        <Route path="/checkout"    element={<CheckoutPage />}    />
        <Route path="/orders"      element={<OrdersPage />}      />
        <Route path="/track/:id"   element={<TrackingPage />}    />
        <Route path="/wishlist"    element={<WishlistPage />}    />
        <Route path="/policy"      element={<PolicyPage />}      />
        <Route path="/returns"     element={<ReturnsPage />}     />
        <Route path="/become-merchant" element={<BecomeMerchantPage />} />

        {/* ── Merchant routes ── */}
        <Route path="/merchant" element={
          <MerchantRoute><MerchantLayout><MerchantDashboard /></MerchantLayout></MerchantRoute>
        } />
        <Route path="/merchant/products" element={
          <MerchantRoute><MerchantLayout><MerchantProducts /></MerchantLayout></MerchantRoute>
        } />
        <Route path="/merchant/products/add" element={
          <MerchantRoute><MerchantLayout><MerchantProductForm /></MerchantLayout></MerchantRoute>
        } />
        <Route path="/merchant/products/edit/:id" element={
          <MerchantRoute><MerchantLayout><MerchantProductForm /></MerchantLayout></MerchantRoute>
        } />
        <Route path="/merchant/orders" element={
          <MerchantRoute><MerchantLayout><MerchantOrders /></MerchantLayout></MerchantRoute>
        } />
        <Route path="/merchant/earnings" element={
          <MerchantRoute><MerchantLayout><MerchantEarnings /></MerchantLayout></MerchantRoute>
        } />
        <Route path="/merchant/profile" element={
          <MerchantRoute><MerchantLayout><MerchantProfile /></MerchantLayout></MerchantRoute>
        } />

        {/* ── Admin routes ── */}
        <Route path="/admin" element={
          <AdminRoute><AdminLayout><AdminDashboard /></AdminLayout></AdminRoute>
        } />
        <Route path="/admin/merchants" element={
          <AdminRoute><AdminLayout><AdminMerchants /></AdminLayout></AdminRoute>
        } />
        <Route path="/admin/orders" element={
          <AdminRoute><AdminLayout><AdminOrders /></AdminLayout></AdminRoute>
        } />
        <Route path="/admin/products" element={
          <AdminRoute><AdminLayout><AdminProducts /></AdminLayout></AdminRoute>
        } />
        <Route path="/admin/coupons" element={
          <AdminRoute><AdminLayout><AdminCoupons /></AdminLayout></AdminRoute>
        } />
        <Route path="/admin/returns" element={
          <AdminRoute><AdminLayout><AdminReturns /></AdminLayout></AdminRoute>
        } />
        <Route path="/admin/ceo-agent" element={
          <AdminRoute><AdminLayout><AdminCEOAgent /></AdminLayout></AdminRoute>
        } />
      </Routes>

      {/* Footer — hidden on dashboard pages */}
      {!isDashboard && <Footer />}
    </>
  );
}

export default function App() {
  return (
    <AppProvider>
      <Router>
        <Toaster
          position="top-center"
          toastOptions={{
            duration: 3000,
            style: {
              borderRadius: '16px',
              fontFamily:   'var(--font-body, sans-serif)',
              fontSize:     '14px',
            },
          }}
        />
        <AppLayout />
      </Router>
    </AppProvider>
  );
}
